import { Component, computed, inject, input, output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { DraftCondition } from '@core/filters/path-filter/draft';
import { defaultOperator } from '@core/filters/path-filter/vocabulary/default-operators';
import { keyedName, splitKeyedName } from '@core/filters/path-filter/vocabulary/vocabulary';
import {
  FILTER_OP_SHAPES,
  FilterOpName,
  OpArgument,
  Quantifier,
  fieldPath,
  pathKey,
  segmentName
} from '@core/filters/path-filter/wire';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { ICONS } from '@core/services/icons/icons.service';

import { EntityAutoCompleteComponent } from '@shared/components/row-edition-form/entity-auto-complete/entity-auto-complete.component';
import { SelectItem, SelectValue } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';
import { AvailableLanguages } from '@shared/types/translation.type';

import { PATH_FILTER_COMPOSER_STATE } from '../composer/path-filter-composer-state';
import {
  FIELD_TYPE_ICONS,
  associationLabel,
  fieldLabel,
  operatorLabel
} from '../path-filter-labels';
import { ComposerContext } from '../path-filter.models';
import { PathFilterValueComponent } from '../value/path-filter-value.component';

const ASSOCIATION_ITEM_PREFIX = 'edge:';

@Component({
  selector: 'app-path-filter-condition',
  imports: [
    TranslatePipe,
    SelectComponent,
    EntityAutoCompleteComponent,
    PathFilterValueComponent
  ],
  templateUrl: './path-filter-condition.component.html',
  styles: `
    :host {
      display: block;
    }
  `
})
export class PathFilterConditionComponent {
  condition = input.required<DraftCondition>();
  /** The association the condition sits in, `null` at the root. */
  edge = input<string | null>(null);
  context = input.required<ComposerContext>();
  /** `stacked` is the pill popover: the field is fixed and shown as its heading. */
  layout = input<'row' | 'stacked'>('row');

  conditionChange = output<DraftCondition>();
  associationPicked = output<{ edge: string; quantifier: Quantifier }>();
  removed = output<void>();

  protected readonly ICONS = ICONS;
  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly lang = computed(() => this.currentLang() as AvailableLanguages);
  /** Absent in the pill popover, which has no phrase to link with. */
  private readonly hovered = inject(PATH_FILTER_COMPOSER_STATE, { optional: true })
    ?.hovered;
  protected readonly linked = computed(() => this.hovered?.() === this.condition().id);

  protected readonly fields = computed(() => this.context().vocabulary.fields(this.edge()));
  /** The entity the condition's field belongs to: the filtered one, or the related one an edge reaches. */
  protected readonly ownerEntityType = computed(() => {
    const { vocabulary } = this.context();
    const edge = this.edge();
    return edge === null
      ? vocabulary.entityType
      : (vocabulary.association(edge)?.targetEntityType ?? null);
  });
  protected readonly field = computed(() => {
    const path = this.condition().path;
    return path ? this.context().vocabulary.field(this.edge(), path) : undefined;
  });
  /** The key of a condition on a keyed field, `null` while none is picked. */
  protected readonly key = computed(() => {
    const path = this.condition().path;
    return this.field()?.keyed && path
      ? (splitKeyedName(segmentName(path.segments[0]))?.key ?? null)
      : null;
  });
  protected readonly fieldName = computed(() => {
    const field = this.field();
    return field ? fieldLabel(field, this.lang(), this.translate) : '';
  });
  protected readonly associations = computed(() =>
    this.context().vocabulary.associations(this.edge())
  );
  protected readonly fieldItems = computed<SelectItem[]>(() => {
    const lang = this.lang();
    const related = this.translate.instant('features.data.path_filter.condition.related');
    return [
      ...this.fields().map((field) => ({
        id: field.key,
        label: fieldLabel(field, lang, this.translate),
        icon: FIELD_TYPE_ICONS[field.type]
      })),
      ...this.associations().map((association) => ({
        id: ASSOCIATION_ITEM_PREFIX + association.edge,
        label: associationLabel(association, lang, this.translate),
        icon: ICONS.treeStructure,
        meta: this.translate.instant(
          'features.data.path_filter.cardinality.' + association.cardinality
        ),
        group: related
      }))
    ];
  });
  protected readonly operatorItems = computed<SelectItem[]>(() => {
    this.lang();
    return (this.field()?.operators ?? []).map((op) => ({
      id: op,
      label: operatorLabel(op, this.translate)
    }));
  });
  protected readonly invalid = computed(() => {
    const path = this.condition().path;
    return path !== null && this.context().invalidPaths.includes(pathKey(path));
  });
  protected readonly fieldId = computed(() => `${this.condition().id}-field`);
  protected readonly operatorId = computed(() => `${this.condition().id}-operator`);

  protected onField(key: SelectValue): void {
    const association = this.associations().find(
      (candidate) => ASSOCIATION_ITEM_PREFIX + candidate.edge === key
    );
    if (association) {
      this.associationPicked.emit({
        edge: association.edge,
        quantifier: (association.quantifiers[0] ?? 'any') as Quantifier
      });
      return;
    }
    const field = this.fields().find((candidate) => candidate.key === key);
    if (!field) return;
    this.conditionChange.emit({
      ...this.condition(),
      path: field.path,
      op: defaultOperator(field, this.context().vocabulary),
      arg: null
    });
  }

  /** The key decides which operators apply: one they no longer offer gives way to the default. */
  protected onKey(value: string | string[] | null): void {
    const field = this.field();
    const key = (Array.isArray(value) ? value[0] : value) || null;
    if (!field || key === this.key()) return;
    const path = key
      ? fieldPath(keyedName(segmentName(field.path.segments[0]), key))
      : field.path;
    const next = this.context().vocabulary.field(this.edge(), path);
    if (!next) return;
    const current = this.condition();
    const keeps = current.op !== null && next.operators.includes(current.op);
    this.conditionChange.emit({
      ...current,
      path,
      op: keeps ? current.op : defaultOperator(next, this.context().vocabulary),
      arg: keeps ? current.arg : null
    });
  }

  protected onOperator(value: SelectValue | FilterOpName): void {
    const op = value as FilterOpName | null;
    const current = this.condition();
    if (!op || op === current.op) return;
    const sameShape =
      current.op !== null && FILTER_OP_SHAPES[current.op] === FILTER_OP_SHAPES[op];
    this.conditionChange.emit({ ...current, op, arg: sameShape ? current.arg : null });
  }

  protected onArg(arg: OpArgument | null): void {
    this.conditionChange.emit({ ...this.condition(), arg });
  }
}
